import React from 'react';
import { Order } from '@hero/schema';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from '@hero/web-components';
import { RouteArrowDisplay } from '@hero/web-components';
import { formatDateMain } from '@hero/core';

export interface OrderModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal open state changes */
  onOpenChange: (open: boolean) => void;
  /** The order to display */
  order: Order;
  /** Modal title */
  title: string;
  /** Optional subtitle (defaults to order ref) */
  subtitle?: string;
  /** Modal content (children) */
  children: React.ReactNode;
  /** Footer action buttons */
  footer?: React.ReactNode;
  /** Optional custom footer actions (if not provided, shows default Cancel/Save) */
  actions?: {
    cancel?: {
      label?: string;
      onClick: () => void;
      disabled?: boolean;
    };
    save?: {
      label?: string;
      onClick: () => void;
      disabled?: boolean;
      loading?: boolean;
    };
  };
  /** Modal size */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Additional className for DialogContent */
  className?: string;
  /** Prevent closing modal when clicking outside */
  preventOutsideClose?: boolean;
}

/**
 * Reusable Order Modal Component
 * 
 * Provides a consistent modal layout for order-related modals with:
 * - Header with title, subtitle (ref), route display (From → To), and pickup date
 * - Content area for custom content
 * - Footer with action buttons
 * 
 * Similar to admin panel's order modals but reusable across dashboards.
 * 
 * @example
 * ```tsx
 * <OrderModal
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   order={order}
 *   title="Edit My Ref"
 *   actions={{
 *     cancel: { onClick: () => setIsOpen(false) },
 *     save: { 
 *       onClick: handleSave, 
 *       loading: isSaving 
 *     }
 *   }}
 * >
 *   <Input value={ref} onChange={e => setRef(e.target.value)} />
 * </OrderModal>
 * ```
 */
export const OrderModal: React.FC<OrderModalProps> = ({
  open,
  onOpenChange,
  order,
  title,
  subtitle,
  children,
  footer,
  actions,
  size = 'md',
  className = '',
  preventOutsideClose = false,
}) => {
  // Format pickup date as "Jan 16"
  const pickupDateFormatted = order.dates?.pickup?.from
    ? formatDateMain({
        date: new Date(order.dates.pickup.from),
        format: 'date',
      })
    : null;

  // Get order ref
  const orderRef = order.ref?.main || order.ref?.short || 'N/A';

  // Generate right side content: route, ref • date (all together on the right)
  const rightSideContent = (
    <div className="text-end space-y-1">
      {/* Route display (From → To) */}
      {order.destinations && order.destinations.length > 0 && (
        <div className="font-medium">
          <RouteArrowDisplay
            origin={order.destinations[0]}
            destinations={order.destinations.slice(1)}
            size="small"
          />
        </div>
      )}
      {/* Ref • Date */}
      {pickupDateFormatted && (
        <div className="text-xs text-muted-foreground">
          {orderRef} • {pickupDateFormatted}
        </div>
      )}
    </div>
  );

  // Get size classes for DialogContent
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
  };

  // Default footer if actions are provided
  const defaultFooter = actions ? (
    <>
      {actions.cancel && (
        <Button variant="outline" onClick={actions.cancel.onClick} disabled={actions.cancel.disabled}>
          {actions.cancel.label || 'Cancel'}
        </Button>
      )}
      {actions.save && (
        <Button onClick={actions.save.onClick} disabled={actions.save.disabled || actions.save.loading}>
          {actions.save.loading ? 'Saving...' : actions.save.label || 'Save'}
        </Button>
      )}
    </>
  ) : null;

  const handleOpenChange = (newOpen: boolean) => {
    // Prevent closing if preventOutsideClose is true and we're trying to close
    if (!newOpen && preventOutsideClose) {
      return;
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={`${sizeClasses[size]} ${className}`}
        onInteractOutside={preventOutsideClose ? e => e.preventDefault() : undefined}
        onEscapeKeyDown={preventOutsideClose ? e => e.preventDefault() : undefined}>
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl font-semibold">{title}</DialogTitle>
              {subtitle ? (
                <DialogDescription className="text-sm text-muted-foreground mt-1">
                  {subtitle}
                </DialogDescription>
              ) : (
                <DialogDescription className="sr-only">
                  {title}
                </DialogDescription>
              )}
            </div>
            <div className="flex-shrink-0 text-sm text-muted-foreground pe-8">
              {rightSideContent}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 py-4 px-4 overflow-y-auto overflow-x-hidden flex-1 min-h-0">
          {children}
        </div>

        {(footer || defaultFooter) && (
          <DialogFooter className="flex-shrink-0">{footer || defaultFooter}</DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
